import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Loader2, X } from "lucide-react";
import { AppointmentJoin } from "@/repositories/appointments.repository";
import { toast } from "sonner";
import { CANCELLATION_REASON_MAX_LENGTH } from "@shyne/shared/api";
import { fieldActionMessage } from "@/lib/appointment-field-actions";
import { cancelAppointment } from "@/services/appointment-cancellation.service";

interface CancelAppointmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  appointment: AppointmentJoin;
  onSuccess?: (updatedAppointment: AppointmentJoin) => void;
  onRefetch?: () => void;
}

export default function CancelAppointmentModal({
  isOpen,
  onClose,
  appointment,
  onSuccess,
  onRefetch,
}: CancelAppointmentModalProps) {
  const [cancellationNotes, setCancellationNotes] = useState("");
  const [loading, setLoading] = useState(false);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setCancellationNotes("");
    }
  }, [isOpen]);

  const handleSubmit = async () => {
    if (!cancellationNotes.trim()) {
      toast.error("Please provide a cancellation reason");
      return;
    }

    setLoading(true);
    try {
      const updatedAppointment = await cancelAppointment(appointment.id, cancellationNotes);
      toast.success("Appointment cancelled successfully");
      if (onSuccess) {
        onSuccess(updatedAppointment);
      }
      if (onRefetch) {
        onRefetch();
      }
      onClose();
    } catch (error) {
      console.error("Failed to cancel appointment:", error);
      toast.error(fieldActionMessage(error, "Failed to cancel appointment"));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <X className="h-5 w-5 text-destructive" />
            Cancel Appointment
          </DialogTitle>
          <DialogDescription>
            Please provide a reason for cancelling this appointment. This action cannot be undone.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 rounded-md p-3">
            <p className="text-sm text-red-800 dark:text-red-200">
              <strong>Warning:</strong> Cancelling this appointment will notify all participants and update the status
              to "cancelled".
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="cancellation-notes">Cancellation Reason *</Label>
            <Textarea
              id="cancellation-notes"
              placeholder="Please provide a reason for cancellation..."
              value={cancellationNotes}
              onChange={(e) => setCancellationNotes(e.target.value)}
              rows={4}
              maxLength={CANCELLATION_REASON_MAX_LENGTH}
              disabled={loading}
              className="resize-none"
            />
            <p className="text-sm text-muted-foreground">
              This reason will be saved and visible to PMC and vendors.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={handleSubmit} disabled={!cancellationNotes.trim() || loading}>
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                Cancelling...
              </>
            ) : (
              "Cancel Appointment"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

